import {Injectable} from '@angular/core';

/**
 * The downloaded broadcasts, read straight off the disk the admin has plugged
 * in — including when the page itself is served from ballscore.app.
 *
 * The other route to a recording, `/local-video/{id}`, only works where the
 * Express server can see the files, which is the analyser's own machine and
 * nowhere else: on Cloud Run there are no disks and every request is a 404.
 * That made the review queue useless for anything needing the picture, because
 * reviewing happens on ballscore.app and the 885 GB of season sit on two
 * external drives.
 *
 * The File System Access API closes that gap without moving a byte anywhere.
 * The admin points the browser at the folder once; the handle is kept in
 * IndexedDB, the file is read locally and played from an object URL, which
 * seeks like any other video because the browser streams it off the disk
 * rather than loading it. Nothing is uploaded, and the server is not involved.
 *
 * Two things to know before relying on it:
 *
 * - **Chrome and Edge only** (`showDirectoryPicker`, 86+). Firefox and Safari
 *   have no directory picker, so there the server route and the copied path
 *   remain the only options — `supported` says which world you are in.
 * - **Permission is not kept across browsing sessions.** Chrome hands the
 *   handle back from IndexedDB but drops the grant, so the first recording of
 *   the day costs one click on *Reconnect*; `requestPermission` needs a user
 *   gesture, which is exactly why that button exists instead of a silent
 *   retry.
 */

/** The bits of the File System Access API the TypeScript DOM lib does not have. */
interface PermissionCapableHandle extends FileSystemHandle {
  queryPermission(options?: { mode?: 'read' | 'readwrite' }): Promise<PermissionState>;

  requestPermission(options?: { mode?: 'read' | 'readwrite' }): Promise<PermissionState>;
}

type RecordingsFolder = FileSystemDirectoryHandle & PermissionCapableHandle;

const DB_NAME = 'ballscore-recordings';
const DB_VERSION = 1;
const STORE = 'folders';
const KEY = 'roots';

/** Where the admin stands with the disk, and what the UI should offer. */
export type RecordingsAccess =
/** No folder has ever been picked here. */
  'none'
  /** A folder is remembered, but this browsing session has not been granted it. */
  | 'prompt'
  /** Ready — a recording can be opened without asking. */
  | 'granted'
  /** No directory picker in this browser. */
  | 'unsupported';

@Injectable({providedIn: 'root'})
export class LocalRecordingsService {

  private folders: RecordingsFolder[] | null = null;

  get supported(): boolean {
    return typeof (window as any).showDirectoryPicker === 'function';
  }

  /**
   * What the page can do right now, asked without prompting anybody. Safe to
   * call on every render: it only reads IndexedDB and the existing grants.
   */
  async access(): Promise<RecordingsAccess> {
    if (!this.supported) {
      return 'unsupported';
    }
    const folders = await this.load();
    if (!folders.length) {
      return 'none';
    }
    const states = await Promise.all(folders.map(folder => this.stateOf(folder)));
    return states.includes('granted') ? 'granted' : 'prompt';
  }

  /**
   * Ask for a folder. Called from a click, because the picker will not open
   * otherwise. Picking a second folder adds it — the season lives on two
   * drives and either may hold the game in front of the reviewer.
   */
  async connect(): Promise<RecordingsAccess> {
    if (!this.supported) {
      return 'unsupported';
    }
    let picked: RecordingsFolder;
    try {
      picked = await (window as any).showDirectoryPicker({id: 'ballscore-recordings', mode: 'read'});
    } catch (e) {
      return this.access();       // the admin closed the picker
    }
    const folders = await this.load();
    const known = await Promise.all(folders.map(folder => folder.isSameEntry(picked)));
    if (!known.includes(true)) {
      folders.push(picked);
      await this.save(folders);
    }
    return this.access();
  }

  /** Re-grant a remembered folder for this session. Needs a click, see above. */
  async grant(): Promise<RecordingsAccess> {
    const folders = await this.load();
    for (const folder of folders) {
      try {
        await folder.requestPermission({mode: 'read'});
      } catch (e) {
        // A folder whose drive is unplugged throws; the others may still work.
      }
    }
    return this.access();
  }

  /**
   * An object URL for the recording, or null when the disk does not have it.
   * The caller owns the URL and must revoke it — see the player.
   */
  async open(videoId: number): Promise<string | null> {
    if ((await this.access()) !== 'granted') {
      return null;
    }
    for (const folder of await this.load()) {
      if ((await this.stateOf(folder)) !== 'granted') {
        continue;
      }
      try {
        const handle = await folder.getFileHandle(`${videoId}.mp4`);
        return URL.createObjectURL(await handle.getFile());
      } catch (e) {
        // Not in this folder, or the drive went away mid-review. Try the next.
      }
    }
    return null;
  }

  /** Forget every folder. For the admin who picked the wrong one. */
  async forget(): Promise<void> {
    this.folders = [];
    await this.save([]);
  }

  // --- storage -------------------------------------------------------------

  private async stateOf(folder: RecordingsFolder): Promise<PermissionState> {
    try {
      return await folder.queryPermission({mode: 'read'});
    } catch (e) {
      return 'denied';
    }
  }

  private async load(): Promise<RecordingsFolder[]> {
    if (this.folders) {
      return this.folders;
    }
    try {
      const database = await this.database();
      this.folders = await new Promise<RecordingsFolder[]>((resolve, reject) => {
        const request = database.transaction(STORE, 'readonly').objectStore(STORE).get(KEY);
        request.onsuccess = () => resolve(request.result ?? []);
        request.onerror = () => reject(request.error);
      });
    } catch (e) {
      // A browser with IndexedDB switched off still works, it just forgets the
      // folder on every reload. Losing the feature would be worse than that.
      this.folders = [];
    }
    return this.folders;
  }

  private async save(folders: RecordingsFolder[]): Promise<void> {
    this.folders = folders;
    try {
      const database = await this.database();
      await new Promise<void>((resolve, reject) => {
        const transaction = database.transaction(STORE, 'readwrite');
        transaction.objectStore(STORE).put(folders, KEY);
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      });
    } catch (e) {
      // See above: remembered or not, the session in hand keeps working.
    }
  }

  private database(): Promise<IDBDatabase> {
    return new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => request.result.createObjectStore(STORE);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
}
